import React from "react";
import {
  Box, Text, Flex, Spinner, Button, Icon,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Input, InputGroup, InputLeftElement, InputRightElement, IconButton,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

export function StoreList({
  displayedStores,
  stores,
  searchQuery,
  handleSearch,
  clearSearch,
  currentPage,
  setCurrentPage,
  pageSize,
  loadingStores,
  selectedStoreDetails,
  onStoreClick,
}) {
  return (
    <Box
      flex="1"
      bg="white"
      borderRadius="xl"
      overflow="hidden"
      boxShadow="sm"
      border="1px solid"
      borderColor="gray.200"
    >
      {/* Header + Search */}
      <Box p={4} borderBottom="1px solid" borderColor="gray.200">
        <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={3}>
          Stores
        </Text>
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search stores..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            bg="white"
            borderColor="gray.300"
            focusBorderColor="red.500"
            _placeholder={{ color: "gray.400" }}
          />
          {searchQuery && (
            <InputRightElement>
              <IconButton
                aria-label="Clear search"
                size="sm"
                icon={<CloseIcon />}
                onClick={clearSearch}
                variant="ghost"
              />
            </InputRightElement>
          )}
        </InputGroup>
      </Box>

      {/* Table + Pagination */}
      <Box p={0} display="flex" height="500px" flexDirection="column">
        {loadingStores ? (
          <Flex justify="center" align="center" height="400px">
            <Spinner size="lg" color="red.600" />
          </Flex>
        ) : (
          <>
            <Box flex="1" overflow="auto">
              <TableContainer>
                <Table variant="simple" size="md">
                  <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th fontWeight="bold" color="gray.700" borderColor="gray.200">
                        Store Name
                      </Th>
                      <Th fontWeight="bold" color="gray.700" borderColor="gray.200">
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {displayedStores
                      .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                      .map((store, index) => (
                        <Tr
                          key={store.id || index}
                          role="group"
                          _hover={{ bg: "red.500", color: "white" }}
                          cursor="pointer"
                          onClick={() => onStoreClick(store)}
                          bg={
                            selectedStoreDetails?.id === store.id
                              ? "red.50"
                              : "transparent"
                          }
                        >
                          <Td borderColor="gray.200">
                            <Text
                              fontWeight="medium"
                              color="gray.800"
                              _groupHover={{ color: "white" }}
                            >
                              {store.storeName || "-"}
                            </Text>
                          </Td>
                          <Td borderColor="gray.200">
                            <Button
                              size="sm"
                              color="red.500"
                              bg="white"
                              _groupHover={{ color: "white", bg: "red.600" }}
                              _hover={{ transform: "scale(1.08)", boxShadow: "lg" }}
                              transition="all 0.2s ease-in-out"
                              colorScheme="red"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                onStoreClick(store);
                              }}
                            >
                              View
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            {/* Pagination */}
            <Flex
              justify="space-between"
              align="center"
              px={4}
              py={3}
              borderTop="1px solid"
              borderColor="gray.200"
              bg="gray.50"
            >
              <Text fontSize="sm" color="gray.600">
                {displayedStores.length > 0
                  ? `Showing ${(currentPage - 1) * pageSize + 1}–${Math.min(
                      currentPage * pageSize,
                      displayedStores.length
                    )} of ${displayedStores.length}${
                      searchQuery ? ` (filtered from ${stores.length})` : ""
                    }`
                  : "No stores"}
              </Text>
              <Flex align="center" gap={2}>
                <Button
                  size="md"
                  variant="outline"
                  colorScheme="red"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  isDisabled={currentPage === 1}
                  leftIcon={<Icon as={FiChevronLeft} />}
                >
                  Prev
                </Button>
                <Text fontSize="sm" color="gray.600" fontWeight="medium" px={1}>
                  {currentPage} / {Math.ceil(displayedStores.length / pageSize) || 1}
                </Text>
                <Button
                  size="md"
                  variant="outline"
                  colorScheme="red"
                  onClick={() => setCurrentPage((p) => p + 1)}
                  isDisabled={currentPage >= Math.ceil(displayedStores.length / pageSize)}
                  rightIcon={<Icon as={FiChevronRight} />}
                >
                  Next
                </Button>
              </Flex>
            </Flex>
          </>
        )}
      </Box>
    </Box>
  );
}
